import { useState, useEffect, useCallback, useMemo, Fragment } from "react";
import { api, API, fmt, fmtDec, errMsg, openServerFile } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader } from "@/components/shared";
import { Gauge, Egg, AlertTriangle, Bird, FileDown, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";

const iso = (d) => {
  const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return z.toISOString().slice(0, 10);
};
const BREED_DOT = { kirmizi: "bg-red-500", tintin: "bg-amber-500", beyaz: "bg-slate-300 border border-slate-400" };

const yieldTone = (y) =>
  y <= 0 ? "bg-secondary text-muted-foreground"
    : y >= 80 ? "bg-green-500/15 text-green-600 dark:text-green-400"
      : y >= 75 ? "bg-amber-500/15 text-amber-600 dark:text-amber-400"
        : "bg-red-500/15 text-red-600 dark:text-red-400";
const fireTone = (f) =>
  f <= 5 ? "bg-green-500/15 text-green-600 dark:text-green-400"
    : f > 7 ? "bg-red-500/15 text-red-600 dark:text-red-400"
      : "bg-amber-500/15 text-amber-600 dark:text-amber-400";

const YBadge = ({ v, testid }) => (
  <span data-testid={testid} className={`inline-flex items-center justify-center min-w-[54px] px-2 py-0.5 rounded-full text-xs font-bold tabular-nums ${yieldTone(v)}`}>
    %{fmtDec(v)}
  </span>
);
const FBadge = ({ v, testid }) => (
  <span data-testid={testid} className={`inline-flex items-center justify-center min-w-[54px] px-2 py-0.5 rounded-full text-xs font-bold tabular-nums ${fireTone(v)}`}>
    %{fmtDec(v)}
  </span>
);

export default function YieldLoss() {
  const { coops, myCoops, isOperator } = useApp();
  const [preset, setPreset] = useState("today");
  const [cStart, setCStart] = useState(iso(new Date()));
  const [cEnd, setCEnd] = useState(iso(new Date()));
  const [locId, setLocId] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const allLocations = coops.filter((c) => c.type === "location");
  const locations = useMemo(() => {
    if (!isOperator) return allLocations;
    const allowed = new Set(myCoops.map((c) => c.parent_id));
    return allLocations.filter((l) => allowed.has(l.id));
  }, [coops, myCoops, isOperator]);

  const { start, end } = useMemo(() => {
    const today = new Date();
    if (preset === "today") return { start: iso(today), end: iso(today) };
    if (preset === "week") { const s = new Date(today); s.setDate(s.getDate() - 6); return { start: iso(s), end: iso(today) }; }
    if (preset === "month") { const s = new Date(today.getFullYear(), today.getMonth(), 1); return { start: iso(s), end: iso(today) }; }
    return { start: cStart, end: cEnd };
  }, [preset, cStart, cEnd]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = `?start=${start}&end=${end}${locId !== "all" ? `&location_id=${locId}` : ""}`;
      const { data } = await api.get(`/dashboard/yield-loss${q}`);
      setData(data);
    } catch (e) { toast.error(errMsg(e)); }
    setLoading(false);
  }, [start, end, locId]);
  useEffect(() => { load(); }, [load]);

  const doExport = (format) => {
    const token = localStorage.getItem("token");
    const q = `?format=${format}&start=${start}&end=${end}${locId !== "all" ? `&location_id=${locId}` : ""}&token=${token}`;
    openServerFile(`${API}/export/yield-loss${q}`, `Randıman & Fire (${format.toUpperCase()})`);
  };

  const kpi = data?.kpi;
  const fb = kpi?.fire_breakdown || {};

  const chartData = useMemo(() => {
    if (!data) return [];
    return data.locations.flatMap((l) =>
      l.subcoops.map((s) => ({
        name: s.coop_name.includes(" - ") ? s.coop_name.split(" - ")[1] : s.coop_name,
        "Randıman %": s.yield_pct,
        "Fire Oranı %": s.fire_rate,
      }))
    );
  }, [data]);

  return (
    <div data-testid="yield-loss-page">
      <PageHeader title="Randıman & Fire Takibi" subtitle="Kümes bazlı günlük verimlilik ve yumurta fire analizi" />

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        <div className="inline-flex rounded-lg border border-border bg-card p-1" data-testid="date-presets">
          {[["today", "Bugün"], ["week", "Son 7 Gün"], ["month", "Bu Ay"], ["custom", "Özel"]].map(([k, l]) => (
            <button key={k} onClick={() => setPreset(k)} data-testid={`preset-${k}`}
              className={`px-3 h-8 rounded-md text-sm font-medium transition-colors ${preset === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`}>
              {l}
            </button>
          ))}
        </div>
        {preset === "custom" && (
          <div className="flex items-center gap-2">
            <input type="date" value={cStart} onChange={(e) => setCStart(e.target.value)} data-testid="custom-start" className="h-9 rounded-lg border border-input bg-background px-3 text-sm focus:ring-2 focus:ring-primary outline-none" />
            <span className="text-muted-foreground text-sm">—</span>
            <input type="date" value={cEnd} onChange={(e) => setCEnd(e.target.value)} data-testid="custom-end" className="h-9 rounded-lg border border-input bg-background px-3 text-sm focus:ring-2 focus:ring-primary outline-none" />
          </div>
        )}
        <select value={locId} onChange={(e) => setLocId(e.target.value)} data-testid="location-filter" className="h-9 rounded-lg border border-input bg-background px-3 text-sm font-medium focus:ring-2 focus:ring-primary outline-none">
          <option value="all">Tüm Kümesler</option>
          {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
        <div className="flex-1" />
        <button onClick={() => doExport("pdf")} data-testid="export-pdf" className="h-9 px-3 inline-flex items-center gap-1.5 rounded-lg border border-input bg-background text-sm font-medium hover:bg-secondary transition-colors">
          <FileDown size={15} /> PDF
        </button>
        <button onClick={() => doExport("xlsx")} data-testid="export-excel" className="h-9 px-3 inline-flex items-center gap-1.5 rounded-lg border border-input bg-background text-sm font-medium hover:bg-secondary transition-colors">
          <FileSpreadsheet size={15} /> Excel
        </button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="rounded-xl border border-border bg-card p-4" data-testid="kpi-avg-yield">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-lg flex items-center justify-center bg-primary/10 text-primary"><Gauge size={18} /></div>
            <span className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground leading-tight">Ort. İşletme Randımanı</span>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <span className="font-head text-3xl font-bold tabular-nums">%{fmtDec(kpi?.avg_yield || 0)}</span>
            <span className={`h-3 w-3 rounded-full ${(kpi?.avg_yield || 0) >= 80 ? "bg-green-500" : (kpi?.avg_yield || 0) >= 75 ? "bg-amber-500" : "bg-red-500"}`} />
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-4" data-testid="kpi-production">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-lg flex items-center justify-center bg-sky-500/10 text-sky-500"><Egg size={18} /></div>
            <span className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground leading-tight">Toplam Üretim</span>
          </div>
          <div className="mt-3 font-head text-2xl font-bold tabular-nums">{fmt(kpi?.total_production_cartons || 0)} <span className="text-sm text-muted-foreground font-medium">Krt</span></div>
          <div className="mt-1 text-[11px] text-muted-foreground">{fmt(kpi?.total_production_adet || 0)} adet · Sağlam {fmt(kpi?.net_healthy || 0)}</div>
        </div>

        <div className="rounded-xl border border-border bg-card p-4" data-testid="kpi-fire">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-lg flex items-center justify-center bg-amber-500/10 text-amber-500"><AlertTriangle size={18} /></div>
            <span className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground leading-tight">Toplam Fire</span>
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="font-head text-2xl font-bold tabular-nums">{fmt(kpi?.total_fire_cartons || 0)} <span className="text-sm text-muted-foreground font-medium">Krt</span></span>
            <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${fireTone(kpi?.fire_rate || 0)}`}>%{fmtDec(kpi?.fire_rate || 0)}</span>
          </div>
          <div className="mt-2 flex flex-wrap gap-1">
            {[["Kirli", fb.kirli], ["Kırık", fb.kirik], ["Çatlak", fb.damar_catlagi], ["Ayırma", fb.ayirma]].map(([l, v]) => (
              <span key={l} className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">{l}: {fmt(v || 0)}</span>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-4" data-testid="kpi-chickens">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-lg flex items-center justify-center bg-secondary text-muted-foreground"><Bird size={18} /></div>
            <span className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground leading-tight">Canlı Tavuk / Ölüm</span>
          </div>
          <div className="mt-3 font-head text-2xl font-bold tabular-nums">{fmt(kpi?.total_chickens || 0)}</div>
          <div className="mt-1 text-[11px] text-destructive font-medium">-{fmt(kpi?.total_deaths || 0)} ölüm</div>
        </div>
      </div>

      {/* Comparison chart */}
      {chartData.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-5 mb-6" data-testid="yl-compare-chart">
          <h3 className="font-head font-semibold mb-4">Kümes Karşılaştırması — Randıman & Fire Oranı (%)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 50 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} angle={-25} textAnchor="end" interval={0} height={70} />
              <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} width={44} unit="%" />
              <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Randıman %" fill="#16a34a" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Fire Oranı %" fill="#f59e0b" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Matrix */}
      <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="yield-loss-matrix">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-secondary/60 text-left">
                <Th>Kümes / Lokasyon</Th><Th>Çeşit</Th>
                <Th className="text-right">Canlı Tavuk</Th><Th className="text-right">Ölüm</Th>
                <Th className="text-right">Üretim (Krt)</Th><Th className="text-center">Randıman</Th>
                <Th className="text-right">Kirli</Th><Th className="text-right">Kırık</Th>
                <Th className="text-right">D.Çatlağı</Th><Th className="text-right">Ayırma</Th>
                <Th className="text-right">Fire (Krt)</Th><Th className="text-center">Fire Oranı</Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={12} className="px-3 py-10 text-center text-muted-foreground">Yükleniyor…</td></tr>
              ) : !data || data.locations.length === 0 ? (
                <tr><td colSpan={12} className="px-3 py-10 text-center text-muted-foreground">Seçilen dönem için üretim kaydı bulunamadı.</td></tr>
              ) : (
                <>
                  {data.locations.map((loc) => (
                    <Fragment key={loc.location_id}>
                      <tr className="bg-primary/10 border-t border-border font-semibold" data-testid={`yl-loc-${loc.location_id}`}>
                        <td className="px-3 py-2.5 font-head font-bold text-primary uppercase tracking-wide text-xs">{loc.location_name} (Ana)</td>
                        <td className="px-3 py-2.5 text-muted-foreground">—</td>
                        <td className="px-3 py-2.5 text-right tabular-nums">{fmt(loc.chickens)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-destructive">{fmt(loc.deaths)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums">{fmt(loc.total_cartons)}</td>
                        <td className="px-3 py-2.5 text-center"><YBadge v={loc.yield_pct} testid={`yl-loc-yield-${loc.location_id}`} /></td>
                        <td className="px-3 py-2.5 text-right tabular-nums">{fmt(loc.fire.kirli)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums">{fmt(loc.fire.kirik)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums">{fmt(loc.fire.damar_catlagi)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums">{fmt(loc.fire.ayirma)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums font-bold">{fmt(loc.total_fire_cartons)}</td>
                        <td className="px-3 py-2.5 text-center"><FBadge v={loc.fire_rate} /></td>
                      </tr>
                      {loc.subcoops.map((sc) => (
                        <tr key={sc.coop_id} className="border-t border-border hover:bg-secondary/30" data-testid={`yl-sub-${sc.coop_id}`}>
                          <td className="px-3 py-2.5 pl-6 text-muted-foreground">↳ {sc.coop_name}</td>
                          <td className="px-3 py-2.5 whitespace-nowrap">
                            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-secondary">
                              <span className={`h-2 w-2 rounded-full ${BREED_DOT[sc.breed] || "bg-slate-400"}`} />{sc.breed_label}
                            </span>
                          </td>
                          <td className="px-3 py-2.5 text-right tabular-nums">{fmt(sc.chickens)}</td>
                          <td className="px-3 py-2.5 text-right tabular-nums text-destructive">{fmt(sc.deaths)}</td>
                          <td className="px-3 py-2.5 text-right tabular-nums font-medium">{fmt(sc.total_cartons)}</td>
                          <td className="px-3 py-2.5 text-center"><YBadge v={sc.yield_pct} testid={`yl-sub-yield-${sc.coop_id}`} /></td>
                          <td className="px-3 py-2.5 text-right tabular-nums">{fmt(sc.fire.kirli)}</td>
                          <td className="px-3 py-2.5 text-right tabular-nums">{fmt(sc.fire.kirik)}</td>
                          <td className="px-3 py-2.5 text-right tabular-nums">{fmt(sc.fire.damar_catlagi)}</td>
                          <td className="px-3 py-2.5 text-right tabular-nums">{fmt(sc.fire.ayirma)}</td>
                          <td className="px-3 py-2.5 text-right tabular-nums font-medium">{fmt(sc.total_fire_cartons)}</td>
                          <td className="px-3 py-2.5 text-center"><FBadge v={sc.fire_rate} /></td>
                        </tr>
                      ))}
                    </Fragment>
                  ))}
                  <tr className="border-t-2 border-primary/40 bg-primary/10 font-bold" data-testid="yl-grand-total">
                    <td className="px-3 py-3 uppercase tracking-wide">🏆 Genel Toplam</td>
                    <td className="px-3 py-3 text-muted-foreground">—</td>
                    <td className="px-3 py-3 text-right tabular-nums">{fmt(data.grand_total.chickens)}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-destructive">{fmt(data.grand_total.deaths)}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{fmt(data.grand_total.total_cartons)}</td>
                    <td className="px-3 py-3 text-center"><YBadge v={data.grand_total.yield_pct} testid="yl-grand-yield" /></td>
                    <td className="px-3 py-3 text-right tabular-nums">{fmt(data.grand_total.fire.kirli)}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{fmt(data.grand_total.fire.kirik)}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{fmt(data.grand_total.fire.damar_catlagi)}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{fmt(data.grand_total.fire.ayirma)}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{fmt(data.grand_total.total_fire_cartons)}</td>
                    <td className="px-3 py-3 text-center"><FBadge v={data.grand_total.fire_rate} /></td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const Th = ({ children, className = "" }) => <th className={`px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap ${className}`}>{children}</th>;
